import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { KpiCard, Panel, VerdictBadge } from "@/lovable-demo/components/bits";
import { ParetoChart } from "@/lovable-demo/components/charts";
import { causePareto, recordsForProgram, rpnDelta } from "@/lovable-demo/lib/warranty/aggregate";
import { recordApDelta } from "@/lovable-demo/lib/warranty/action-priority";
import { ApBadge } from "@/lovable-demo/components/compliance-verdict";
import { fmtCompactCurrency, fmtNumber } from "@/lovable-demo/lib/warranty/calc";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";

export const Route = createFileRoute("/demo/prevention")({
  head: () => ({
    meta: [
      { title: "Prevention Intelligence — Broadview Warranty" },
      {
        name: "description",
        content:
          "Pareto of warranty failure causes with PFMEA and control plan links plus read-across tracking across platforms.",
      },
      { property: "og:title", content: "Prevention Intelligence — Broadview Warranty" },
      {
        property: "og:description",
        content:
          "Pareto of warranty failure causes with PFMEA and control plan links plus read-across tracking across platforms.",
      },
    ],
  }),
  component: Prevention,
});

function Prevention() {
  const { data, activeProgramId } = useWarranty();
  const records = recordsForProgram(data, activeProgramId);
  const pareto = causePareto(records);
  const vital = pareto.filter((p, i) => i === 0 || (pareto[i - 1]?.cumulative ?? 0) < 80);
  const readAcross = records.flatMap((r) => r.readAcross.map((ra) => ({ ...ra, record: r })));
  const openReadAcross = readAcross.filter((ra) => ra.status !== "cleared");
  const highAfter = records.filter((r) => recordApDelta(r.pfmea).after === "H").length;
  const avgReduction = Math.round(
    records.reduce((a, r) => a + rpnDelta(r).reduction, 0) / Math.max(records.length, 1),
  );

  return (
    <AppShell
      title="Prevention Intelligence"
      subtitle="Where the loop stops recurrence: causes, controls and read-across"
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Vital-few causes"
          value={String(vital.length)}
          hint={`Drive 80% of ${fmtNumber(pareto.reduce((a, b) => a + b.value, 0))} claims`}
        />
        <KpiCard
          label="Records still at AP High"
          value={String(highAfter)}
          tone={highAfter ? "warn" : "good"}
          hint={`After action · average RPN (legacy) reduction ${avgReduction}%`}
        />
        <KpiCard
          label="Read-across items open"
          value={String(openReadAcross.length)}
          tone="warn"
          hint={`${readAcross.length} screened across platforms`}
        />
        <KpiCard
          label="Read-across exposure"
          value={fmtCompactCurrency(openReadAcross.reduce((a, b) => a + b.exposure, 0))}
          tone="bad"
          hint="Untreated similar-platform risk"
        />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <Panel
          title="Pareto of failure causes"
          subtitle="Aggregate claims by proven or leading cause"
        >
          <ParetoChart items={pareto.map((p) => ({ label: p.label, value: p.value }))} />
        </Panel>

        <Panel
          title="PFMEA / control plan links"
          subtitle="Highest Action Priority per record, before and after action · RPN (legacy) second"
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">Record</th>
                  <th className="px-2 py-2 text-left font-medium">Control technique</th>
                  <th className="px-2 py-2 text-center font-medium">AP before → after</th>
                  <th className="px-2 py-2 text-right font-medium">RPN (legacy)</th>
                  <th className="px-2 py-2 text-right font-medium">RPN Δ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {records.map((r) => {
                  const d = rpnDelta(r);
                  const ap = recordApDelta(r.pfmea);
                  return (
                    <tr key={r.id}>
                      <td className="px-2 py-2.5">
                        <Link
                          to="/demo/problem-record/$id"
                          params={{ id: r.id }}
                          className="hover:text-primary"
                        >
                          <span className="num block text-xs text-primary">{r.code}</span>
                          <span className="block max-w-[220px] truncate">{r.component}</span>
                        </Link>
                      </td>
                      <td className="px-2 py-2.5 text-xs text-muted-foreground">
                        {r.pfmea[0]?.controlTechnique ?? "—"}
                      </td>
                      <td className="px-2 py-2.5 text-center">
                        <ApBadge ap={ap.before} /> → <ApBadge ap={ap.after} />
                      </td>
                      <td className="num px-2 py-2.5 text-right text-muted-foreground">
                        {d.before} → {d.after}
                      </td>
                      <td className="num px-2 py-2.5 text-right">{d.reduction}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <div className="mt-4">
        <Panel
          title="Read-across tracking"
          subtitle="Similar platforms and components screened for the same failure mode"
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">Source record</th>
                  <th className="px-2 py-2 text-left font-medium">Program / platform</th>
                  <th className="px-2 py-2 text-left font-medium">Shared component</th>
                  <th className="px-2 py-2 text-right font-medium">Units</th>
                  <th className="px-2 py-2 text-right font-medium">Exposure</th>
                  <th className="px-2 py-2 text-left font-medium">Prevention action</th>
                  <th className="px-2 py-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {readAcross.map((ra) => (
                  <tr key={`${ra.record.id}-${ra.id}`} className="hover:bg-elevated/60">
                    <td className="px-2 py-2.5">
                      <Link
                        to="/demo/problem-record/$id"
                        params={{ id: ra.record.id }}
                        className="num text-xs text-primary hover:underline"
                      >
                        {ra.record.code}
                      </Link>
                    </td>
                    <td className="px-2 py-2.5">
                      <span className="block">{ra.program}</span>
                      <span className="block text-xs text-muted-foreground">{ra.platform}</span>
                    </td>
                    <td className="px-2 py-2.5 text-muted-foreground">{ra.sharedComponent}</td>
                    <td className="num px-2 py-2.5 text-right">{fmtNumber(ra.unitsInService)}</td>
                    <td className="num px-2 py-2.5 text-right">
                      {fmtCompactCurrency(ra.exposure)}
                    </td>
                    <td className="px-2 py-2.5 text-xs text-muted-foreground">
                      {ra.preventionAction}
                    </td>
                    <td className="px-2 py-2.5">
                      <VerdictBadge verdict={ra.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </AppShell>
  );
}
